/**
 * Where each DAA pair is, from Blender's incident log (ENG-345).
 *
 * The `daa_alerts` topic has no positions, and the simulated drones behind the
 * test encounters never publish telemetry to MQTT — they exist only inside
 * Blender. Its incident log carries both aircraft's position for every pair on
 * every DAA tick, so it is the fallback for any end the map has no live
 * position for.
 *
 * Polled through the same-origin `/oi-api` proxy (which holds the tenant
 * token), and only while an encounter is active. The first call after an idle
 * spell asks for the last 15 s (~1.5 MB at ten encounters); later calls ask only
 * for rows newer than the newest one seen, less a small overlap for rows a tick
 * commits late — the log is keyed by the server's own timestamps, so local clock
 * skew does not matter.
 */
import { config } from '@/lib/config';
import { daaPairKey } from '@/lib/mqtt/schemas/daaAlerts';
import { useDaaAlertsStore } from '@/lib/store/daaAlerts';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

const POLL_MS = 1500;
const FIRST_WINDOW_MS = 15_000;
const OVERLAP_MS = 3000;
/** A position older than this is no longer where the aircraft is. */
export const DAA_POSITION_FRESH_MS = 15_000;

type LngLat = [number, number];

export interface DaaPositions {
  own: LngLat;
  intruder: LngLat;
  /** The server's timestamp for the row these came from. */
  rowAt: string;
  /** When we received it. */
  receivedAt: number;
  cpaTimeS: number | null;
  ownshipHeadingDeg: number | null;
}

interface PositionsStore {
  byKey: Record<string, DaaPositions>;
  /** Newest row timestamp seen — the next poll starts just before it. */
  newestRowAt: string | null;
  error: string | null;
  ingest: (rows: unknown, now?: number) => void;
  prune: (now?: number) => void;
  setError: (error: string | null) => void;
  clear: () => void;
}

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

export const useDaaPositionsStore = create<PositionsStore>()(
  subscribeWithSelector((set) => ({
    byKey: {},
    newestRowAt: null,
    error: null,
    ingest: (rows, now = Date.now()) => {
      if (!Array.isArray(rows)) return;
      set((s) => {
        let byKey = s.byKey;
        let newest = s.newestRowAt;
        for (const r of rows as Record<string, unknown>[]) {
          const flight = str(r.ownship_operation_id);
          const intruder = str(r.intruder_icao);
          const at = str(r.timestamp);
          const ownLat = num(r.ownship_lat);
          const ownLng = num(r.ownship_lon);
          const intLat = num(r.intruder_lat);
          const intLng = num(r.intruder_lon);
          if (!flight || !intruder || !at) continue;
          if (at > (newest ?? '')) newest = at;
          if (ownLat == null || ownLng == null || intLat == null || intLng == null) continue;
          const key = daaPairKey(flight, intruder);
          // ISO timestamps from one server order as strings.
          if ((byKey[key]?.rowAt ?? '') >= at) continue;
          if (byKey === s.byKey) byKey = { ...s.byKey };
          byKey[key] = {
            own: [ownLng, ownLat],
            intruder: [intLng, intLat],
            rowAt: at,
            receivedAt: now,
            cpaTimeS: num(r.cpa_time_seconds),
            ownshipHeadingDeg: num(r.ownship_heading_deg),
          };
        }
        return byKey === s.byKey && newest === s.newestRowAt
          ? s
          : { byKey, newestRowAt: newest, error: null };
      });
    },
    prune: (now = Date.now()) =>
      set((s) => {
        let byKey: Record<string, DaaPositions> | null = null;
        for (const [k, p] of Object.entries(s.byKey)) {
          if (now - p.receivedAt <= DAA_POSITION_FRESH_MS) continue;
          byKey ??= { ...s.byKey };
          delete byKey[k];
        }
        return byKey ? { byKey } : s;
      }),
    setError: (error) => set((s) => (s.error === error ? s : { error })),
    clear: () => set({ byKey: {}, newestRowAt: null, error: null }),
  })),
);

/** Query string for the next poll: the last 15 s on the first call, then just
 *  after the newest row seen. */
export function incidentStartDate(newestRowAt: string | null, now = Date.now()): string {
  const first = now - FIRST_WINDOW_MS;
  const newest = newestRowAt ? Date.parse(newestRowAt) : Number.NaN;
  const from = Number.isFinite(newest) ? Math.max(first, newest - OVERLAP_MS) : first;
  return new Date(from).toISOString();
}

const hasLiveEncounter = () =>
  Object.values(useDaaAlertsStore.getState().byKey).some((e) => e.status === 'active');

/** Poll the incident log while an encounter is active. Returns a stop function. */
export function startIncidentPoller(): () => void {
  if (config.isMock) return () => {};
  let inFlight = false;
  const tick = async () => {
    const store = useDaaPositionsStore.getState();
    store.prune();
    if (inFlight) return;
    if (!hasLiveEncounter()) {
      // Idle: the next burst starts from a fresh 15 s window, not an old cursor.
      if (store.newestRowAt) useDaaPositionsStore.setState({ newestRowAt: null });
      return;
    }
    inFlight = true;
    try {
      const since = encodeURIComponent(incidentStartDate(store.newestRowAt));
      const res = await fetch(
        `${config.oiApi.base}/detect_and_avoid_ops/logs/incident/?start_date=${since}`,
        { headers: { Accept: 'application/json' }, signal: AbortSignal.timeout(20_000) },
      );
      if (!res.ok) throw new Error(`incident log ${res.status}`);
      useDaaPositionsStore.getState().ingest(await res.json());
    } catch (e) {
      useDaaPositionsStore.getState().setError(e instanceof Error ? e.message : String(e));
    } finally {
      inFlight = false;
    }
  };
  const id = window.setInterval(tick, POLL_MS);
  return () => window.clearInterval(id);
}
